import { useMemo, useState, type ReactNode } from 'react';
import {
  Award,
  BarChart3,
  BookOpen,
  Calculator,
  FileText,
  Languages,
  Leaf,
  MessageSquare,
  Monitor,
  Palette,
  Printer,
  Trophy,
  X,
} from 'lucide-react';

import { PrintDocument } from '@/components/ui/PrintDocument';
import { useInstituteBranding } from '@/features/institute/store/branding.store';
import { cn } from '@/lib/utils';
import type { StudentExamMark } from '@/types/student';
import {
  calculateGrade,
  isPraiseworthy,
  paperGrade,
  percentageOf,
  remarksOf,
  type ExamGroup,
} from './examGroups';

/**
 * The report cards tab.
 *
 * This product has no report card of its own — no table holds one, and an
 * academic year is not divided into terms. So a "report card" here is an exam
 * the child has marks for, built from the same groups as the results view.
 * Nothing on this tab is invented: what the school has not recorded is shown
 * as a dash or an empty state, never a sample figure.
 */

export interface ParentReportCardsTabProps {
  /** Most recent first, as `groupExams` returns them. */
  examGroups: ExamGroup[];
  ranks: Record<string, { rank: number; totalStudents: number }>;
  childName?: string;
  /** Null only while the results have not loaded — the card then shows a dash. */
  admissionNumber: string | null;
  childFirstName: string;
  displayClass: string;
  academicYearName: string;
  classTeacher: string | null;
}

/** Everything one exam's report card is drawn from, on screen and on paper alike. */
export interface ReportCardDetails {
  group: ExamGroup;
  rank: { rank: number; totalStudents: number } | null;
  childName?: string;
  admissionNumber: string | null;
  displayClass: string;
  academicYearName: string;
  classTeacher: string | null;
}

const BAR_COLORS = [
  'bg-[#8b5cf6]',
  'bg-[#10b981]',
  'bg-[#06b6d4]',
  'bg-[#f59e0b]',
  'bg-[#ec4899]',
  'bg-[#eab308]',
];

/** A subject's icon from its name — a hint, so anything unrecognised gets a page. */
function subjectIcon(name: string) {
  const lower = name.toLowerCase();
  if (lower.includes('math')) return Calculator;
  if (lower.includes('eng')) return BookOpen;
  if (lower.includes('evs') || lower.includes('sci')) return Leaf;
  if (lower.includes('hin') || lower.includes('tel') || lower.includes('lang')) return Languages;
  if (lower.includes('comp')) return Monitor;
  if (lower.includes('art')) return Palette;
  return FileText;
}

/** One paper as a row: marks, percentage and grade, or Absent ("AB"). */
function paperRow(paper: StudentExamMark, index: number) {
  const obtained = paper.isAbsent ? null : paper.marksObtained;
  // Null, not 0, without a score — so no bar is drawn for a paper nobody scored.
  const pct =
    obtained !== null && paper.maxMarks > 0 ? Math.round((obtained / paper.maxMarks) * 100) : null;

  // Held to the paper's own pass mark, and coloured by the band it lands in.
  const grade = paperGrade(paper);

  return {
    key: paper.uuid,
    name: paper.subject.name,
    icon: subjectIcon(paper.subject.name),
    maxMarks: paper.maxMarks,
    obtained,
    isAbsent: paper.isAbsent,
    pct,
    // Absence is not a grade — "AB", as the results table and a printed card write it.
    grade: paper.isAbsent ? 'AB' : (grade?.grade ?? '—'),
    gradeColor: grade?.color ?? 'text-ink-3',
    gradeBadge: grade?.badgeClass ?? 'bg-surface-2 text-ink-2 border-line',
    barColor: BAR_COLORS[index % BAR_COLORS.length],
  };
}

export function ParentReportCardsTab({
  examGroups,
  ranks,
  childName,
  admissionNumber,
  childFirstName,
  displayClass,
  academicYearName,
  classTeacher,
}: ParentReportCardsTabProps) {
  const [selected, setSelected] = useState<ExamGroup | null>(null);
  const [printing, setPrinting] = useState<ReportCardDetails | null>(null);

  // Every paper of the year, so the overall figures are the year's and not one exam's.
  const overall = useMemo(() => {
    const totals = percentageOf(examGroups.flatMap((group) => group.papers));
    return { ...totals, grade: calculateGrade(totals.percentage) };
  }, [examGroups]);

  const latest = examGroups[0] ?? null;
  const latestRows = useMemo(() => (latest ? latest.papers.map(paperRow) : []), [latest]);
  const latestRemarks = useMemo(() => remarksOf(latest), [latest]);


  if (!latest) {
    return (
      <div className="rounded-2xl border border-line bg-surface p-12 text-center shadow-2xs">
        <FileText className="h-10 w-10 text-ink-3 mx-auto mb-3" />
        <h3 className="text-base font-bold text-ink">No report cards yet</h3>
        <p className="mt-1 text-sm text-ink-2 max-w-md mx-auto">
          A report card appears here for each exam once {childFirstName || 'your child'}&rsquo;s
          marks are published by the school.
        </p>
      </div>
    );
  }

  const latestRank = ranks[latest.name] ?? null;
  const selectedCard: ReportCardDetails | null = selected
    ? {
        group: selected,
        rank: ranks[selected.name] ?? null,
        childName,
        admissionNumber,
        displayClass,
        academicYearName,
        classTeacher,
      }
    : null;

  return (
    <div className="flex flex-col gap-5">
      {/* 1. TOP STAT CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="group relative block overflow-hidden rounded-2xl sm:rounded-3xl border border-purple-200/90 dark:border-slate-800 bg-purple-50/90 dark:bg-slate-900 p-3.5 sm:p-5 shadow-xs hover:shadow-md transition-[transform,box-shadow,border-color] duration-200">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[14px] font-bold text-purple-800 dark:text-purple-400 leading-tight">
                Report Cards
              </p>
              <p className="mt-0.5 text-2xl font-black text-purple-600 dark:text-purple-400 tracking-tight tabular-nums truncate">
                {examGroups.length}
              </p>
              <p className="mt-0.5 sm:mt-1 text-[11px] sm:text-xs font-semibold text-purple-600/90 dark:text-slate-400">
                {examGroups.length === 1 ? 'Exam with marks' : 'Exams with marks'} this year
              </p>
            </div>
            <span
              className="grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-xl sm:rounded-2xl font-extrabold shadow-2xs text-white"
              style={{ backgroundColor: '#7c3aed' }}
              aria-hidden
            >
              <FileText className="h-5 w-5" />
            </span>
          </div>
        </div>
        <div className="group relative block overflow-hidden rounded-2xl sm:rounded-3xl border border-emerald-200/90 dark:border-slate-800 bg-emerald-50/90 dark:bg-slate-900 p-3.5 sm:p-5 shadow-xs hover:shadow-md transition-[transform,box-shadow,border-color] duration-200">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[14px] font-bold text-emerald-800 dark:text-emerald-400 leading-tight">
                Overall Grade
              </p>
              <p
                className={cn(
                  'mt-0.5 text-2xl font-black tracking-tight tabular-nums truncate',
                  overall.grade?.color ?? 'text-ink-3',
                )}
              >
                {overall.grade?.grade ?? '—'}
              </p>
              <p className="mt-0.5 sm:mt-1 text-[11px] sm:text-xs font-semibold text-emerald-700/90 dark:text-slate-400">
                {overall.grade?.label ?? 'Nothing scored yet'}
              </p>
            </div>
            <span
              className="grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-xl sm:rounded-2xl font-extrabold shadow-2xs text-white"
              style={{ backgroundColor: '#059669' }}
              aria-hidden
            >
              <Award className="h-5 w-5" />
            </span>
          </div>
        </div>
        <div className="group relative block overflow-hidden rounded-2xl sm:rounded-3xl border border-cyan-200/90 dark:border-slate-800 bg-cyan-50/90 dark:bg-slate-900 p-3.5 sm:p-5 shadow-xs hover:shadow-md transition-[transform,box-shadow,border-color] duration-200">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[14px] font-bold text-cyan-800 dark:text-cyan-400 leading-tight">
                Overall Percentage
              </p>
              <p className="mt-0.5 text-2xl font-black text-cyan-600 dark:text-cyan-400 tracking-tight tabular-nums truncate">
                {overall.percentage === null ? '—' : `${overall.percentage}%`}
              </p>
              <p className="mt-0.5 sm:mt-1 text-[11px] sm:text-xs font-semibold text-cyan-600/90 dark:text-slate-400">
                Across every exam this year
              </p>
            </div>
            <span
              className="grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-xl sm:rounded-2xl font-extrabold shadow-2xs text-white"
              style={{ backgroundColor: '#0891b2' }}
              aria-hidden
            >
              <BarChart3 className="h-5 w-5" />
            </span>
          </div>
        </div>
        <div className="group relative block overflow-hidden rounded-2xl sm:rounded-3xl border border-amber-200/90 dark:border-slate-800 bg-amber-50/90 dark:bg-slate-900 p-3.5 sm:p-5 shadow-xs hover:shadow-md transition-[transform,box-shadow,border-color] duration-200">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[14px] font-bold text-amber-800 dark:text-amber-400 leading-tight">
                Class Rank
              </p>
              <div className="mt-0.5 flex items-baseline gap-1 truncate">
                <span className="text-2xl font-black text-amber-600 dark:text-amber-400 tracking-tight tabular-nums">
                  {latestRank?.rank ?? '—'}
                </span>
                {latestRank && (
                  <span className="text-base font-bold text-amber-800/70 dark:text-slate-400">
                    / {latestRank.totalStudents}
                  </span>
                )}
              </div>
              <p className="mt-0.5 sm:mt-1 text-[11px] sm:text-xs font-semibold text-amber-700/90 dark:text-slate-400">
                {latestRank ? `In ${latest.name}` : `Not ranked on ${latest.name} yet`}
              </p>
            </div>
            <span
              className="grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-xl sm:rounded-2xl font-extrabold shadow-2xs text-white"
              style={{ backgroundColor: '#d97706' }}
              aria-hidden
            >
              <Trophy className="h-5 w-5" />
            </span>
          </div>
        </div>
      </div>

      {/* 2. MAIN TWO-COLUMN CONTENT */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
        {/* --- LEFT COLUMN: REPORT CARDS TABLE --- */}
        <div className="lg:col-span-8 flex flex-col gap-4">
          <div className="rounded-2xl border border-line bg-surface p-5 shadow-2xs flex flex-col gap-4">
            <div className="flex flex-col gap-0.5 pb-3 border-b border-line">
              <h3 className="text-base font-bold text-ink">Report Cards</h3>
              <p className="text-xs text-ink-2 font-medium">
                One for each exam {childFirstName || 'your child'} has marks for. Open one to view
                or print it.
              </p>
            </div>

            <div className="overflow-x-auto rounded-xl border border-line">
              <table className="w-full border-collapse text-left text-xs">
                <thead>
                  <tr className="border-b border-line bg-surface-2">
                    <th className="py-2.5 px-3 text-center w-10 text-xs font-bold text-ink-3">#</th>
                    <th className="py-2.5 px-3 text-sm font-bold text-ink">Examination</th>
                    <th className="py-2.5 px-3 text-sm font-bold text-ink-2">Period</th>
                    <th className="py-2.5 px-3 text-center text-sm font-bold text-ink-2">Status</th>
                    <th className="py-2.5 px-3 text-center text-sm font-bold text-ink-2">
                      Overall %
                    </th>
                    <th className="py-2.5 px-3 text-center text-sm font-bold text-ink-2">Grade</th>
                    <th className="py-2.5 px-3 text-center text-sm font-bold text-ink-2">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-line">
                  {examGroups.map((group, idx) => (
                    <tr
                      key={group.name}
                      className="hover:bg-surface-2/40 transition-colors cursor-pointer"
                      onClick={() => setSelected(group)}
                    >
                      <td className="py-3 px-3 text-center text-xs font-semibold text-ink-3">
                        {idx + 1}
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2.5">
                          <div className="h-9 w-9 rounded-xl flex items-center justify-center border shrink-0 bg-purple-500/15 border-purple-500/30">
                            <FileText className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                          </div>
                          <div className="flex flex-col">
                            <span className="font-bold text-sm text-ink">{group.name}</span>
                            <span className="text-2xs font-bold text-ink-3 tracking-wider uppercase">
                              {group.papers.length}{' '}
                              {group.papers.length === 1 ? 'subject' : 'subjects'}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-3 text-sm font-medium text-ink-2 whitespace-nowrap">
                        {group.dateRange}
                      </td>
                      <td className="py-3 px-3 text-center whitespace-nowrap">
                        {/* Only published exams reach this tab; absence is shown per paper. */}
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30">
                          Published
                        </span>
                      </td>
                      <td className="py-3 px-3 text-center font-bold text-sm text-ink whitespace-nowrap">
                        {group.percentage === null ? '—' : `${group.percentage}%`}
                      </td>
                      <td className="py-3 px-3 text-center font-extrabold text-sm whitespace-nowrap">
                        <span className={group.gradeInfo?.color ?? 'text-ink-3'}>
                          {group.gradeInfo?.grade ?? '—'}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-center whitespace-nowrap">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setSelected(group);
                          }}
                          className="px-3 py-1 rounded-lg text-xs font-bold bg-accent/15 text-accent border border-accent/30 hover:bg-accent/25 transition-colors cursor-pointer"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* --- RIGHT COLUMN: LATEST REPORT CARD & SUBJECT-WISE MARKS --- */}
        <div className="lg:col-span-4 flex flex-col gap-4">
          <div className="rounded-2xl border border-line bg-surface p-5 shadow-2xs flex flex-col gap-4">
            <div className="flex items-center justify-between pb-3 border-b border-line">
              <h3 className="text-base font-bold text-ink">Latest Report Card</h3>
              <button
                type="button"
                onClick={() => setSelected(latest)}
                className="text-xs font-bold text-accent hover:underline cursor-pointer"
              >
                View &rarr;
              </button>
            </div>

            <button
              type="button"
              onClick={() => setSelected(latest)}
              className="flex items-center justify-between gap-3 p-3 rounded-xl bg-surface-2/40 border border-line hover:border-accent-line transition-colors cursor-pointer text-left"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-xl bg-purple-500/15 border border-purple-500/30 flex items-center justify-center shrink-0">
                  <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="text-sm font-bold text-ink truncate">{latest.name}</h4>
                  <p className="text-xs text-ink-2 font-medium">{latest.dateRange}</p>
                </div>
              </div>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-2xs font-bold bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 shrink-0">
                Published
              </span>
            </button>

            <div className="grid grid-cols-3 gap-2 pt-1 text-center">
              <div className="flex flex-col gap-0.5">
                <span className="text-2xs font-bold text-ink-2 uppercase tracking-wider">
                  Overall %
                </span>
                <span className="text-xl font-black text-ink">
                  {latest.percentage === null ? '—' : `${latest.percentage}%`}
                </span>
              </div>
              <div className="flex flex-col gap-0.5 border-x border-line">
                <span className="text-2xs font-bold text-ink-2 uppercase tracking-wider">
                  Grade
                </span>
                <span className={cn('text-xl font-black', latest.gradeInfo?.color ?? 'text-ink-3')}>
                  {latest.gradeInfo?.grade ?? '—'}
                </span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-2xs font-bold text-ink-2 uppercase tracking-wider">
                  Class Rank
                </span>
                <span className="text-xl font-black text-ink">
                  {latestRank ? `${latestRank.rank} / ${latestRank.totalStudents}` : '—'}
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-line bg-surface p-5 shadow-2xs flex flex-col gap-4">
            <div className="flex items-center justify-between pb-3 border-b border-line">
              <h3 className="text-base font-bold text-ink">Subject-wise Marks</h3>
              <span className="text-xs font-semibold text-ink-3 truncate">{latest.name}</span>
            </div>

            <div className="flex flex-col gap-3">
              {latestRows.map((row) => (
                <div key={row.key} className="flex flex-col gap-1">
                  <div className="flex items-center justify-between text-xs font-semibold">
                    <div className="flex items-center gap-1.5 text-ink min-w-0">
                      <row.icon className="h-3.5 w-3.5 text-ink-2 shrink-0" />
                      <span className="text-sm truncate">{row.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-ink text-sm">
                        {row.isAbsent ? 'Absent' : `${row.obtained ?? '—'} / ${row.maxMarks}`}
                      </span>
                      <span className={cn('text-sm font-extrabold min-w-5 text-right', row.gradeColor)}>
                        {row.grade}
                      </span>
                    </div>
                  </div>
                  <div className="h-2 w-full bg-surface-2 rounded-full overflow-hidden">
                    {row.pct !== null && (
                      <div
                        className={cn('h-full rounded-full transition-all', row.barColor)}
                        style={{ width: `${row.pct}%` }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 3. BOTTOM ROW: ENCOURAGEMENT & TEACHERS' REMARKS */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-stretch">
        <div className="lg:col-span-7 rounded-2xl border border-line bg-surface p-5 sm:p-6 shadow-2xs flex flex-col sm:flex-row items-center gap-6 overflow-hidden relative">
          {/* Vector SVG with 3D Books and Graduation Cap */}
          <div className="relative w-40 h-32 shrink-0 flex items-center justify-center">
            <svg
              viewBox="0 0 160 130"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-full h-full drop-shadow-md select-none"
            >
              <defs>
                <linearGradient id="rcBookBottom" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#4338ca" />
                  <stop offset="100%" stopColor="#312e81" />
                </linearGradient>
                <linearGradient id="rcBookMid" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#6366f1" />
                  <stop offset="100%" stopColor="#4f46e5" />
                </linearGradient>
                <linearGradient id="rcBookTop" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#818cf8" />
                  <stop offset="100%" stopColor="#6366f1" />
                </linearGradient>
                <linearGradient id="rcCapGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#6366f1" />
                  <stop offset="100%" stopColor="#3730a3" />
                </linearGradient>
              </defs>

              {/* Bottom Book */}
              <polygon points="20,110 110,110 130,90 40,90" fill="url(#rcBookBottom)" />
              <polygon points="20,110 110,110 110,121 20,121" fill="#312e81" />
              <polygon points="110,110 130,90 130,101 110,121" fill="#e0e7ff" />

              {/* Middle Book */}
              <polygon points="25,93 105,93 123,75 43,75" fill="url(#rcBookMid)" />
              <polygon points="25,93 105,93 105,103 25,103" fill="#4338ca" />
              <polygon points="105,93 123,75 123,85 105,103" fill="#e0e7ff" />

              {/* Top Book */}
              <polygon points="30,77 100,77 116,61 46,61" fill="url(#rcBookTop)" />
              <polygon points="30,77 100,77 100,86 30,86" fill="#4f46e5" />
              <polygon points="100,77 116,61 116,70 100,86" fill="#e0e7ff" />

              {/* Graduation Cap (Mortarboard) */}
              <ellipse cx="73" cy="58" rx="16" ry="7" fill="#1e1b4b" />
              <polygon
                points="73,33 116,47 73,61 30,47"
                fill="url(#rcCapGrad)"
                stroke="#a5b4fc"
                strokeWidth="1.5"
              />
              <ellipse cx="73" cy="47" rx="3.5" ry="2.5" fill="#facc15" />
              <path
                d="M73,47 Q90,49 96,60 Q100,67 100,75"
                stroke="#facc15"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
              />
              <polygon points="98,75 102,75 104,83 96,83" fill="#facc15" />
            </svg>
          </div>

          {/* Worded from the year's grade, so a child who is struggling is
              encouraged rather than congratulated on a result they did not get. */}
          <div className="flex flex-col gap-1.5 flex-1">
            <p className="text-lg sm:text-xl font-black italic tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-indigo-600 to-cyan-600 dark:from-purple-400 dark:via-indigo-300 dark:to-cyan-300 font-serif leading-tight">
              &ldquo; {isPraiseworthy(overall.grade) ? 'Well done' : 'Keep going'}
              {childFirstName ? `, ${childFirstName}` : ''}! &rdquo;
            </p>
            <p className="text-xs sm:text-sm font-semibold text-ink-2 mt-1">
              Consistent effort today builds a brighter tomorrow.
            </p>
          </div>
        </div>

        <div className="lg:col-span-5 rounded-2xl border border-line bg-surface p-5 shadow-2xs flex flex-col gap-3.5">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-full bg-purple-500/15 border border-purple-500/30 flex items-center justify-center shrink-0">
              <MessageSquare className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="text-base font-bold text-ink">Teachers&rsquo; Remarks</h3>
          </div>

          <RemarksList remarks={latestRemarks} examName={latest.name} />
        </div>
      </div>

      {/* 4. REPORT CARD PREVIEW */}
      {selectedCard && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${selectedCard.group.name} report card`}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fade-in"
        >
          <div className="relative w-full max-w-2xl rounded-2xl border border-line bg-surface p-6 shadow-2xl flex flex-col gap-5 animate-scale-in max-h-[90vh] overflow-y-auto">
            <ReportCardBody
              {...selectedCard}
              headerAction={
                <button
                  type="button"
                  aria-label="Close"
                  onClick={() => setSelected(null)}
                  className="p-1.5 rounded-lg text-ink-3 hover:text-ink hover:bg-surface-2 transition-colors cursor-pointer"
                >
                  <X className="h-5 w-5" />
                </button>
              }
            />

            <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-line">
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="px-4 py-2 rounded-xl text-sm font-bold border border-line text-ink hover:bg-surface-2 transition-colors cursor-pointer"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => setPrinting(selectedCard)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold bg-accent text-white hover:opacity-90 transition-opacity shadow-2xs cursor-pointer"
              >
                <Printer className="h-3.5 w-3.5" />
                <span className="text-sm tracking-wide">Print Report Card</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <ReportCardPrint target={printing} onFinished={() => setPrinting(null)} />
    </div>
  );
}

/**
 * One exam's report card: who it is for, every paper, the totals and what the
 * teachers wrote.
 *
 * The same body is drawn in the preview dialog and sent to paper, so the card a
 * family prints is the card they were looking at. Headed with the school's own
 * name from branding — left off rather than replaced by the product's name while
 * branding has not loaded, since a card headed with the wrong name is worse than
 * one headed with none.
 */
function ReportCardBody({
  group,
  rank,
  childName,
  admissionNumber,
  displayClass,
  academicYearName,
  classTeacher,
  headerAction,
}: ReportCardDetails & { headerAction?: ReactNode }) {
  const schoolName = useInstituteBranding((state) => state.branding?.displayName ?? null);
  const rows = group.papers.map(paperRow);
  const remarks = remarksOf(group);

  return (
    <>
      <div className="flex items-start justify-between pb-4 border-b border-line">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl flex items-center justify-center border shrink-0 bg-purple-500/15 border-purple-500/30">
            <FileText className="h-6 w-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            {schoolName && <p className="text-sm font-bold text-ink-2">{schoolName}</p>}
            <h3 className="text-lg font-bold text-ink">{group.name}</h3>
            <p className="text-xs text-ink-3 mt-0.5">
              {academicYearName && `Academic session ${academicYearName} • `}
              {group.dateRange}
            </p>
          </div>
        </div>

        {headerAction}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-3.5 rounded-xl bg-surface-2/60 border border-line text-xs">
        <div>
          <span className="text-ink-3 font-semibold block text-sm">Student</span>
          <span className="font-bold text-ink text-sm">{childName || '—'}</span>
        </div>
        <div>
          <span className="text-ink-3 font-semibold block text-sm">Admission No.</span>
          <span className="font-bold text-ink text-sm">{admissionNumber || '—'}</span>
        </div>
        <div>
          <span className="text-ink-3 font-semibold block text-sm">Class &amp; Sec</span>
          <span className="font-bold text-ink text-sm">{displayClass}</span>
        </div>
        <div>
          <span className="text-ink-3 font-semibold block text-sm">Overall Score</span>
          <span className="font-bold text-ink text-sm">
            {group.percentage === null
              ? '—'
              : `${group.totalObtained} / ${group.totalMax} (${group.percentage}%)`}
          </span>
        </div>
        <div>
          <span className="text-ink-3 font-semibold block text-sm">Grade / Rank</span>
          <span className={cn('font-bold text-sm', group.gradeInfo?.color ?? 'text-ink')}>
            {group.gradeInfo?.grade ?? '—'}
            {rank && ` (Rank ${rank.rank} of ${rank.totalStudents})`}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-bold tracking-wide text-ink-3">Subject-wise marks</h4>
        <div className="overflow-x-auto rounded-xl border border-line">
          <table className="w-full border-collapse text-left text-xs">
            <thead>
              <tr className="border-b border-line bg-surface-2">
                <th className="py-2 px-3 text-center w-10 text-xs font-bold text-ink-3">#</th>
                <th className="py-2 px-3 text-sm font-bold text-ink">Subject</th>
                <th className="py-2 px-3 text-right text-sm font-bold text-ink-2">Max Marks</th>
                <th className="py-2 px-3 text-right text-sm font-bold text-ink">Marks Obtained</th>
                <th className="py-2 px-3 text-right text-sm font-bold text-ink-2">Percentage</th>
                <th className="py-2 px-3 text-center text-sm font-bold text-ink">Grade</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((row, idx) => (
                <tr key={row.key} className="hover:bg-surface-2/40">
                  <td className="py-2.5 px-3 text-center text-xs font-semibold text-ink-3">
                    {idx + 1}
                  </td>
                  <td className="py-2.5 px-3 font-bold text-sm text-ink">{row.name}</td>
                  <td className="py-2.5 px-3 text-right text-sm font-medium text-ink-2">
                    {row.maxMarks}
                  </td>
                  <td className="py-2.5 px-3 text-right text-sm font-bold text-ink">
                    {row.isAbsent ? 'Absent' : (row.obtained ?? '—')}
                  </td>
                  <td className="py-2.5 px-3 text-right text-sm font-semibold text-ink-2">
                    {row.pct === null ? '—' : `${row.pct}%`}
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <span
                      className={cn(
                        'inline-block px-2 py-0.5 rounded-md text-xs font-bold border',
                        row.gradeBadge,
                      )}
                    >
                      {row.grade}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-xl border border-line bg-surface-2/40 p-4 flex flex-col gap-2">
        <span className="text-sm font-bold text-ink-3 tracking-wide">Teachers&rsquo; remarks</span>
        <RemarksList remarks={remarks} examName={group.name} />
        {classTeacher && (
          <div className="pt-3 mt-1 border-t border-line/60 text-sm">
            <span className="font-semibold text-ink">Class Teacher: {classTeacher}</span>
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Sends one report card to the printer, and nothing else.
 *
 * `window.print()` on its own put the whole panel on paper — sidebar, tabs and
 * every other exam. `PrintDocument` prints this card alone, from the preview
 * dialog or the results tab alike, so both print the same sheet.
 */
export function ReportCardPrint({
  target,
  onFinished,
}: {
  target: ReportCardDetails | null;
  onFinished: () => void;
}) {
  // Saved as "Report-Card-<admission no>-<exam>" rather than the screen's title.
  const fileName = target
    ? ['Report-Card', target.admissionNumber, target.group.name]
        .filter((part): part is string => Boolean(part))
        .join('-')
        .replace(/\s+/g, '-')
    : 'Report-Card';

  return (
    <PrintDocument isOpen={target !== null} onFinished={onFinished} fileName={fileName}>
      {target && (
        <div className="flex flex-col gap-5 p-2">
          <ReportCardBody {...target} />
        </div>
      )}
    </PrintDocument>
  );
}

/** The remarks written against one exam, or a line saying there are none. */
function RemarksList({
  remarks,
  examName,
}: {
  remarks: { subject: string; remark: string; author: string | null }[];
  examName: string;
}) {
  if (remarks.length === 0) {
    return <p className="text-sm text-ink-3">No teacher has written a remark on {examName} yet.</p>;
  }

  return (
    <ul className="flex flex-col gap-2.5">
      {remarks.map((item) => (
        <li key={item.subject} className="rounded-xl border border-line bg-surface-2/40 p-3.5">
          <p className="text-sm text-ink-2 leading-relaxed">&ldquo;{item.remark}&rdquo;</p>
          {/* Signed by whoever entered the mark; unsigned when nobody is recorded. */}
          {item.author && <p className="mt-1.5 text-xs font-semibold text-ink">— {item.author}</p>}
        </li>
      ))}
    </ul>
  );
}
